import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router'
import { Button } from '../../platform/primitives/Button'
import { Money } from '../../platform/primitives/Money'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { sharePercent } from './cashflowRules'

export type RankRow = {
  key: string
  name: string
  /** 'Up 12% vs September' | '12 payments' | null for none. */
  sub: string | null
  /** ≥ 0 for a ranked row; negative only when `flag` is set. */
  amountCents: number
  /** The server's share_pct; null hides both the % and the bar. */
  sharePct: number | null
  /** Where the row goes; the name becomes the link and its hit area stretches over the row. */
  to?: string
  /** A category or group whose refunds outweigh its spend: signed amount, a chip, no bar, no %. */
  flag?: 'miscategorised'
}

const DEFAULT_TOP = 6

/**
 * One ranked list: name and an optional sub line, the amount, and the share,
 * with a decorative background bar behind the name. The bar is the SERVER's
 * share of spend — not the row against the largest row — so bars compare
 * across rows and across toggles ("a row's background bar is its share").
 *
 * The bar's width is the one inline declaration: a `--share` custom property
 * the stylesheet reads as `inline-size`. The skin stays in CSS (rule 2).
 *
 * The rows past `top` hide behind one button that stays mounted and only
 * relabels, so focus never has to be moved back to it.
 */
export function RankedList({
  label,
  rows,
  currency,
  top = DEFAULT_TOP,
  showAllNoun,
  shareColumn = true,
}: {
  label: string
  rows: RankRow[]
  currency: string
  top?: number
  /** 'categories' → "Show all 9 categories". */
  showAllNoun: string
  /**
   * Whether the list has a % column at all. False (income: the server sends
   * no share) drops the % track, so the amounts end at the content edge
   * rather than leaving the column's room empty. A list that has the column
   * keeps it on rows without a share, so its amounts stay aligned. It governs
   * the column only: a row that carries a share still draws its share bar.
   */
  shareColumn?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  // A phone drops the % column so the amount keeps its room; the bar still carries the share.
  const phone = useBreakpoint() === 'phone'
  // Flagged rows are a warning: never behind "Show all". The cut and the count are over the ranked rows only.
  const ranked = rows.filter((r) => r.flag === undefined)
  const flagged = rows.filter((r) => r.flag !== undefined)
  const overflow = ranked.length > top
  const shown = [...(overflow && !expanded ? ranked.slice(0, top) : ranked), ...flagged]
  return (
    <div className="cf-rank">
      <ol
        aria-label={label}
        className={['cf-rank__list', phone ? 'cf-rank--phone' : '', shareColumn ? '' : 'cf-rank--no-share'].filter(Boolean).join(' ')}
      >
        {shown.map((r) => (
          <li key={r.key} className={r.flag ? 'cf-rank__row cf-rank__row--flag' : 'cf-rank__row'}>
            {r.sharePct !== null && r.flag === undefined && (
                <span
                  aria-hidden="true"
                  className="cf-rank__bar"
                  style={{ '--share': `${r.sharePct}%` } as CSSProperties}
                />
              )}
            <span className="cf-rank__text">
              {r.to !== undefined ? (
                <Link to={r.to} className="cf-rank__name cf-rank__link">
                  {r.name}
                </Link>
              ) : (
                <span className="cf-rank__name">{r.name}</span>
              )}
              {r.flag === 'miscategorised' && <span className="cf-flag">Miscategorised</span>}
              {r.sub !== null && <span className="cf-rank__sub">{r.sub}</span>}
            </span>
            <span className="cf-rank__amount">
              <Money cents={r.amountCents} currency={currency} display="whole" />
            </span>
            {shareColumn && r.sharePct !== null && r.flag === undefined && !phone && <span className="cf-rank__pct">{sharePercent(r.sharePct)}</span>}
          </li>
        ))}
      </ol>
      {overflow && (
        <Button
          variant="chip"
          size="panel"
          className="cf-rank__more"
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Show fewer' : `Show all ${ranked.length} ${showAllNoun}`}
        </Button>
      )}
    </div>
  )
}
